import { useState, useCallback, useEffect } from 'react';
import { affiliatePartnersService } from '../services/affiliatePartners';
import type {
  AffiliatePartnerItem,
  AffiliatePartnersParams,
} from '../types/affiliatePartners';

interface UseAffiliatePartnersReturn {
  items: AffiliatePartnerItem[];
  total: number;
  loading: boolean;
  error: string | null;
  page: number;
  totalPages: number;
  refetch: () => Promise<void>;
}

export function useAffiliatePartners(
  params: AffiliatePartnersParams
): UseAffiliatePartnersReturn {
  const limit = params.limit || 20;
  const offset = params.offset || 0;

  const [state, setState] = useState<{
    items: AffiliatePartnerItem[];
    total: number;
    loading: boolean;
    error: string | null;
  }>({
    items: [],
    total: 0,
    loading: true,
    error: null,
  });

  const fetch = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const data = await affiliatePartnersService.getPartners(params);
      setState({
        items: Array.isArray(data.partners) ? data.partners : [],
        total: data.total ?? 0,
        loading: false,
        error: null,
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to fetch affiliate partners';
      setState((prev) => ({ ...prev, loading: false, error: message }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.search, params.limit, params.offset]);

  useEffect(() => {
    fetch();
  }, [fetch]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(state.total / limit));

  return { ...state, page, totalPages, refetch: fetch };
}
